import { OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { WizBaseComponent } from '../../../../core/wiz-base-component';
import { WizDatasource } from '../../../../../services/collections/wiz-datasource';
import { WizTextInput } from '../../wiz-text-input/wiz-text-input';
import { WizTextarea } from '../../wiz-text-area/wiz-text-area';
import { WizImage } from '../../../wiz-image/wiz-image';
import { WizVgroup } from '../../../../layouts/wiz-vgroup/wiz-vgroup';
import { TRANSLATION_MODE } from './translation.config';
import { CODE_SPACE, IWizTranslationEntry, VALUE_ENTER, VALUE_ESCAPE, VALUE_TAB, WIZ_EVENT_TYPE} from '@wizeoo/akm-core-lib';
import { WizEventManager } from '../../../../../events/wiz-event.manager';
import { WizEvent } from '../../../../../events/wiz-event';

/**
 * Popup de saisie de libellés traductibles
 * Author : MDI
 */
@Component({
  selector: 'wiz-translation-basic-pop',
  templateUrl: './wiz-translation.basic.pop.html',
  styles: [':host {width: 100%}']
})
export class WizTranslationBasicPop implements AfterViewInit, OnDestroy{

  @ViewChild('TextinputTpl', {read: TemplateRef, static: true}) textinputTpl: TemplateRef<any>;
  @ViewChild('TextareaTpl', {read: TemplateRef, static: true}) textareaTpl: TemplateRef<any>;

  /** le composant input principal */
  @ViewChildren('tiLabel') tiChildren: QueryList<WizTextarea | WizTextInput>;
  /** liste des flag des differentes langues de traduction */
  @ViewChildren('imgFlag') languageIcons: QueryList<WizImage>;

  @ViewChild('inputContainer', {read: WizVgroup}) inputContainer: WizVgroup;

  /**
   * la langue actuellement sélectionnée
   */
  public currentLanguage: IWizTranslationEntry;

  /** WizImage qui a le focus */
  public currentFocusedFlag: WizImage;


  /** Nombre max de drapeau affichés dans la pop */
  private _maxTranslation: number = 5;
  public set maxTranslation(maxT: number) {
    this.setMaxTranslation(maxT);
  }
  public get maxTranslation(): number {
    return this.getMaxTranslation();
  }
  public setMaxTranslation(maxT: number) {
    this._maxTranslation = maxT;
  }

  public getMaxTranslation(): number {
    return this._maxTranslation;
  }

  /**
   * Map des entrées de traduction
   */
  private entryMap: Map<string, IWizTranslationEntry> = new Map();

  /** Composant parent */
  private _parentComponent: WizBaseComponent;
  public set parentComponent(parent: WizBaseComponent) {
    this.setParentComponent(parent);
  }
  public setParentComponent(parent: WizBaseComponent) {
    this._parentComponent = parent;
  }

  /** Nombre max de caractères */
  private _inputMode: TRANSLATION_MODE = 'textinput';
  @Input()
  public set inputMode(mode: TRANSLATION_MODE) {
    this.setInputMode(mode);
  }
  public get inputMode(): TRANSLATION_MODE {
    return this.getInputMode();
  }

  /**
   * methodes getter/setter surchargeables du _wizMaxChars
   */
  protected setInputMode(mode: TRANSLATION_MODE) {
    this._inputMode = mode;
  }
  protected getInputMode() {
    return this._inputMode;
  }

  /**
   * Accés au template à afficher en fonction du mode choisi
   */
  public get template(): TemplateRef<any> {
    switch (this.getInputMode()) {
      case 'textinput':
        return this.textinputTpl;
      case 'textarea':
        return this.textareaTpl;
    }
  }

  /** Collection construite à partir de la datasource,  limitée à 5 langues */
  public trimEntries: IWizTranslationEntry[];

  /** Datasource des entrées à afficher */
  private _entryDS: WizDatasource<IWizTranslationEntry>;
  public get entryDS(): WizDatasource<IWizTranslationEntry> {
    return this.getEntryDS();
  }
  public set entryDS(ds: WizDatasource<IWizTranslationEntry>) {
    this.setEntryDS(ds);
  }
  public getEntryDS(): WizDatasource<IWizTranslationEntry> {
    return this._entryDS ? this._entryDS : new WizDatasource<IWizTranslationEntry>();
  }

  public setEntryDS(ds: WizDatasource<IWizTranslationEntry>): void {
    this._entryDS = ds;
    this.trimEntries = this._entryDS.getWorkedSource().slice(0, this.maxTranslation);
    this.currentLanguage = this.trimEntries[0];
  }

  /**
   * Constructeur
   * @param elementRef pour pose d'écouteur
   * @param document pour écoute keydown
   * @param overlayRef pour fermer la pop
   */
  constructor(public elementRef: ElementRef,
    @Inject(DOCUMENT) protected document: Document, protected overlayRef?: OverlayRef) {
}

  /**
   * Pose d'écouteurs
   */
  public ngAfterViewInit(): void {// Changement dans l'input
    WizEventManager.addListener(this, this.elementRef, WIZ_EVENT_TYPE.CHANGE, this.onLabelChange);

    // Tabulation intra pop up
    WizEventManager.addListener(this, this.elementRef, 'keydown', this.keyDownHandler);
    WizEventManager.addListener(this, this.inputContainer, 'keydown', this.keyDownOnInput);
    this.initFields();
  }

  /**
   * Release d'écouteurs
   */
  public ngOnDestroy(): void {
    WizEventManager.removeListener(this, this.elementRef, WIZ_EVENT_TYPE.CHANGE);
    WizEventManager.removeListener(this, this.elementRef, 'keydown');
    WizEventManager.removeListener(this, this.inputContainer, 'keydown');
  }

  /**
   * Initialisation des champs
   */
  private initFields(): void {
    if (this._entryDS) {
      this.entryDS.getSource().reduce((map: Map<string, IWizTranslationEntry>, entry: IWizTranslationEntry) => {
        map.set(entry.languageCode, entry);
        return map;
      }, this.entryMap);
    }
  }

  /**
   * Changement dans le champ de saisie
   * @param e Evenement change
   */
  private onLabelChange(): void {
    // const ti: WizTextInput = e.component as WizTextInput;
    if (this.currentLanguage) {
      const entry: IWizTranslationEntry = this.entryMap.get(this.currentLanguage.languageCode);
      if (entry) {
        entry.label = this.tiChildren.first.wizValue;
        if (this._parentComponent && this._parentComponent.manageModel) {
          WizEventManager.dispatchEvent(this._parentComponent.elementRef,
            new WizEvent(WIZ_EVENT_TYPE.MODEL_UPDATE, this._parentComponent, entry));
        }
      }
    }
  }

  /**
   * Ecoute clavier pour gestion de la navigation et de la sélection d'horaires
   * @param e Evènement key down
   */
  private keyDownHandler(e: KeyboardEvent): void {
    if (e.key === VALUE_ESCAPE || e.key === VALUE_ENTER) { // Echap, entrée : fermeture
      if (this.overlayRef) {
        this.overlayRef.dispose();
      }
    }
  }

  private keyDownOnInput(event: KeyboardEvent): void {
    if (event.key === VALUE_TAB) {
      event.preventDefault();
      this.languageIcons.first.wizGetFocus();
      this.languageIcons.first.elementRef.nativeElement.focus();
    }
  }
  /**
   * Prise de focus > focus sur la 1ere trad
   */
  public wizFocus(): void {
    setTimeout(() => { // sinon les images ne sont pas encore créés
      if (this.languageIcons && this.languageIcons.first) {
        this.languageIcons.first.wizGetFocus();
        // on focus le champ de saisie
        this.focusInput();
      }
    }, 0);
  }

  /**
   * demande de changement de langue de traduction
   * @param entry la nouvelle langue de traduction
   */
  public changeTranslationLanguage(entry: IWizTranslationEntry): void {
    // on reinitialiser l'input avec la nouvelle langue
    this.currentLanguage = entry;
    this.tiChildren.first.wizValue = entry.label;
    this.currentFocusedFlag = this.languageIcons.find(item => item.id === entry.languageCode)
    // on focus le champ de saisie
    this.focusInput();
  }

  /**
   * focus du champ de saisie
   */
  private focusInput(): void {
    if (this.tiChildren && this.tiChildren.first) {
      this.tiChildren.first.wizFocus();
    }
  }

  /**
   * demande de changement de langue de traduction par clavier; touche entrée ou espace
   * @param event event clavier
   */
  public keydownChangeLanguage(event: KeyboardEvent, entry: IWizTranslationEntry): void {
    if (event.code === CODE_SPACE) {
      this.changeTranslationLanguage(entry);
    }  else if (event.key === VALUE_TAB) { // Le tab ne doit pas faire quitter la pop up
      if (this.currentFocusedFlag.id === this.languageIcons.last.id && !event.shiftKey) {
        event.preventDefault();
        this.languageIcons.first.wizFocus();
      } else if (this.currentFocusedFlag.id === this.languageIcons.first.id && event.shiftKey) {
        event.preventDefault();
        this.languageIcons.last.wizFocus();
      }
    }
  }


  /**
   * Handler de prise de focus pour stockage du composant qui prend ce focus
   * @param e Evenement focus in
   */
  public onFocusIn(e: FocusEvent): void {
    const targetField: string = 'id';
    this.currentFocusedFlag = this.languageIcons.find(item => item.id === e.target[targetField]);
  }
}
